import { useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_MENU_HEIGHT = 192;

/**
 * Fixed-position layout for form dropdowns inside overflow containers.
 */
export default function useFixedDropdownMenu(open, anchorRef, menuHeight = DEFAULT_MENU_HEIGHT) {
  const menuRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [placementReady, setPlacementReady] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      setLayout(null);
      setPlacementReady(false);
      return;
    }

    const el = anchorRef.current;
    if (!el) {
      setLayout(null);
      setPlacementReady(false);
      return;
    }

    setPlacementReady(false);

    const updateLayout = () => {
      const rect = el.getBoundingClientRect();
      const gap = 4;
      const viewportPad = 8;
      const measured = menuRef.current?.offsetHeight;
      const h = measured && measured > 0 ? measured : menuHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      let top = rect.bottom + gap;

      if (spaceBelow < h + gap && rect.top > h + gap + viewportPad) {
        top = Math.max(viewportPad, rect.top - h - gap);
      }

      top = Math.min(Math.max(viewportPad, top), window.innerHeight - h - viewportPad);

      setLayout({
        top,
        left: rect.left,
        width: rect.width,
      });
    };

    updateLayout();

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        updateLayout();
        setPlacementReady(true);
      });
    });

    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
    };
  }, [open, anchorRef, menuHeight]);

  return { menuRef, layout, placementReady };
}
